import {useGenericQuery} from "@/hooks/useQueryHooks";
import type {TChat, TPaginated} from "@/store/types";
import {useInfiniteQuery} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {useState} from "react";
import {useDebounce} from "react-use";

type TBulkMessageInfluencer = {
  uuid: number;
  user: {
    id: number;
    first_name: string;
    last_name: string | null;
  };
  picture: string | null;
  picture320: string | null;
  picture640: string | null;
};

export const CHAT_KEYS = {
  mChatBulkMessage: ["/chat/bulk-message/"],
  mChatImageUpload: ["/chat/upload_image/"],
  mChatMessage: ["/chat/message/"],
  qChatBulkMessageInfluencers: ({campaignId}: {campaignId: number}) => [
    `/campaign/${campaignId}/bulk_message_influencers/`,
  ],
  qChatList: ({companyId}: {companyId: number}) => [`/chat/list/?company_profile_id=${companyId}`],
  qChatMessageList: ({campaignId, influencerUserId}: {campaignId: number; influencerUserId: number}) => [
    `/chat/paginated_messages/?campaign_id=${campaignId}&recipient_id=${influencerUserId}`,
  ],
  qChatsOfCampaign: ({campaignId, search}: {campaignId: number; search?: string}) => [
    `/chat/company_chat/?campaign_id=${campaignId}&search=${search ?? ""}`,
  ],
} as const;

export const useBulkMessageableInfluencersQuery = ({campaignId, isBulk}: {campaignId: number; isBulk?: boolean}) =>
  useGenericQuery<TBulkMessageInfluencer[]>({
    queryKey: CHAT_KEYS.qChatBulkMessageInfluencers({campaignId}),
    enabled: !!campaignId && isBulk,
  });

export const useChatsOfCampaignQuery = ({campaignId, search = ""}: {campaignId: number; search?: string}) => {
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useDebounce(() => setDebouncedSearch(search), 1000, [search]);

  return useInfiniteQuery<
    AxiosResponse<TPaginated<TChat> & {bulk_count: number}>,
    AxiosError,
    {bulkCount: number; results: TChat[]}
  >({
    queryKey: CHAT_KEYS.qChatsOfCampaign({campaignId, search: debouncedSearch}),
    initialPageParam: CHAT_KEYS.qChatsOfCampaign({campaignId, search: debouncedSearch})[0],
    getNextPageParam: lastPage => lastPage.data.next,
    enabled: !!campaignId,
    select: response => ({
      bulkCount: response.pages[0].data.bulk_count,
      results: response.pages.flatMap(page => page.data.results),
    }),
  });
};
